import React from 'react'
import { Bullet, Bullets, Info, Page, Section } from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const PatientFeedback = () => (
  <Page
    title="Patient Feedback"
    intro="Patient feedback has a public submission route so patients or their representatives can give feedback without an AmbuNet account, and an internal governance area to review it."
  >
    <Section title="Public submission">
      <Bullets>
        <Bullet>
          <DocLink url="/patient-feedback" text="Patient Feedback Form" /> is
          the public-facing form patients or their representatives use to
          submit feedback. No login is required.
        </Bullet>
        <Bullet>
          If a patient consents to SMS and gives a contact number on their
          ePCR, submitting the record automatically texts them a link to the
          feedback form (a tier 2 subscription feature).
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Linking feedback to the care record">
      <Bullets>
        <Bullet>
          The SMS link is unique to that ePCR and valid for 60 days. Opening
          it asks the patient (or their representative) to confirm the{' '}
          <strong>patient's surname and date of birth</strong>. If they match,
          the feedback is linked to the care record and the crew on it; if
          not, they can try again (five attempts, then a 15-minute lock) or
          choose <strong>Leave feedback anonymously</strong>.
        </Bullet>
        <Bullet>
          Anonymous feedback stores no link, no token and no identity — it is
          genuinely unattributable. Records where the surname or date of
          birth was marked unknown receive the plain anonymous link.
        </Bullet>
        <Bullet>
          Linked items show a link icon in{' '}
          <DocLink url="/feedback" text="Feedback Review" /> and a{' '}
          <strong>Care Record</strong> box in the drawer with the ePCR number,
          incident date, crew and a <strong>View Care Record</strong> button.
          Linked compliments and complaints also appear on those crew
          members' appraisals.
        </Bullet>
      </Bullets>
      <Info title="Data protection">
        The patient is told what linking means before they confirm, the
        surname and date of birth they enter are compared server-side and
        never stored on the feedback, and nothing from the care record is
        shown back to the patient. Tenants should reference patient feedback
        in their privacy notice and record of processing.
      </Info>
    </Section>

    <Section title="Governance review">
      <Bullets>
        <Bullet>
          Submitted feedback is reviewed from{' '}
          <DocLink url="/feedback" text="Feedback Review" />. The page opens
          on <strong>New</strong> items, with tiles to switch between
          Reviewing, Completed, Compliments, Complaints and everything; a
          patient-experience summary (recommend rate and the share answering
          yes to each question); and a search/filter bar (comments, names,
          ePCR number, crew; relationship; where they met you; linked-only).
          Governance team members also see a{' '}
          <strong>New Patient Feedback</strong> chip on their home dashboard
          while items are unreviewed.
        </Bullet>
        <Bullet>
          Clicking a row opens the review drawer. Each item moves through{' '}
          <strong>New</strong>, <strong>Reviewing</strong>, and{' '}
          <strong>Completed</strong> (click the status chip), can be tagged
          as a <strong>Compliment</strong> and/or <strong>Complaint</strong>,
          escalated into a governance incident, given a{' '}
          <strong>Learning</strong> note (used as CQC evidence), and discussed
          in a comments thread with other governance users.
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        Viewing and updating patient feedback requires the{' '}
        <strong>Governance</strong> team. Submitting feedback is public by
        design.
      </Info>
    </Section>
  </Page>
)

export default PatientFeedback
